import {
    collection,
    addDoc,
    deleteDoc,
    doc,
    query,
    where,
    orderBy,
    getDocs,
    Firestore,
} from "@angular/fire/firestore";
import { SleepUserSegment, UserSegmentSleepState } from "../models/sleep.segment";
import { Injectable } from "@angular/core";
import { UserService } from "./user.service";
import { AggregatesService } from "./aggregates.service";

const DbReferences = {
    version: "v0",
    Sleep: {
        sleepStagesForUser: (userId: string) => `/v0/data/user/${userId}/sleep/sleep-epm/sleepStages`,
        sleepSegmentsForUser: (userId: string) => `/v0/data/user/${userId}/sleep/sleep-segments/sleepSegments`,
        sleepUserSegmentsForUser: (userId: string) => `/v0/data/user/${userId}/sleep/sleep-user-segments/sleepUserSegments`,
    },
};

const Params = {
    start: "start",
    end: "end",
};

@Injectable({ providedIn: 'root' })
export class SleepService {
    constructor(
        private userService: UserService,
        private firestore: Firestore,
        private aggregateService: AggregatesService,
    ) { }

    async sleepUserSegments(start: Date, end: Date, state?: UserSegmentSleepState): Promise<SleepUserSegment[]> {
        const path = DbReferences.Sleep.sleepUserSegmentsForUser(this.userService.user.id);
        const colRef = collection(this.firestore, path);

        const q = query(
            colRef,
            where(Params.end, ">", start.getTime()),
            where(Params.end, "<=", end.getTime()),
            orderBy(Params.end)
        );

        const snap = await getDocs(q);

        let segments: SleepUserSegment[] = snap.docs.map(d =>
            SleepUserSegment.fromJson(d.data(), d.id)
        );

        if (state) {
            segments = segments.filter(seg => seg.state === state);
        }

        return segments;
    }


    async save(sleepUserSegments: SleepUserSegment[]) {
        const path = DbReferences.Sleep.sleepUserSegmentsForUser(this.userService.user.id);
        const colRef = collection(this.firestore, path);

        for (const seg of sleepUserSegments) {
            await addDoc(colRef, seg);
        }
    }

    async deleteUserSegments(segments: SleepUserSegment[]) {
        const path = DbReferences.Sleep.sleepUserSegmentsForUser(this.userService.user.id);
        for (const seg of segments) {
            const docRef = doc(this.firestore, path, seg.objectId);
            await deleteDoc(docRef);
        }
    }

    async deleteUserSegmentsByDate(from: Date, to: Date, state?: UserSegmentSleepState) {
        const segments = await this.sleepUserSegments(from, to, state);
        await this.deleteUserSegments(segments);
    }

    async findAndDeleteUserSegment(segment: SleepUserSegment) {
        const existingSegments = await this.sleepUserSegments(segment.start, segment.end);
        const match = existingSegments.find(
            s => s.start === segment.start && s.end === segment.end && s.state === segment.state
        );
        if (match) {
            await this.deleteUserSegments([match]);
        }
    }

    async saveSleepSegment(
        start: Date | null,
        end: Date | null,
        hoursSlept: number | null,
        state: UserSegmentSleepState
    ): Promise<SleepUserSegment> {
        let resolvedStart: Date | null = null;
        let resolvedEnd: Date | null = null;

        if (start && end) {
            resolvedStart = start;
            resolvedEnd = end;
        } else if (start && hoursSlept) {
            resolvedStart = start;
            resolvedEnd = new Date(start.getTime() + hoursSlept * 3600000);
        } else if (end && hoursSlept) {
            resolvedEnd = end;
            resolvedStart = new Date(end.getTime() - hoursSlept * 3600000);
        } else {
            throw new Error("Invalid time input");
        }

        if (!resolvedStart || !resolvedEnd) {
            throw new Error("Invalid time input");
        }

        const segment = new SleepUserSegment(resolvedStart, resolvedEnd, state);

        await this.findAndDeleteUserSegment(segment);
        await this.save([segment]);
        await this.aggregateService.saveDailyAggregateSleep(segment);

        return segment;
    }
}
